import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { Row, Col, Form, Button, Collapse, Popover } from 'antd';
import { ReactComponent as Plus_Icon } from '../../../../../../assets/images/svg/plus-large.svg';
import { ReactComponent as Minus_Icon } from '../../../../../../assets/images/svg/Minus_Icon.svg';
import { ReactComponent as Disabled_Plus_Icon } from '../../../../../../assets/images/svg/patient_sign_disabled_plus_icon.svg';
import { APPLICATION_ID, INDIAN_DATE_FORMAT, INVENTROY_CONSTANTS, PATIENT_CONSENT_LIST, PATIENT_SIGNATURE_CONSTANTS } from '../../../../../../shared/constants/commonConstants';
import ConsentDeclarationModal from './ConsentSignatureUploadModal';
import { RootState } from '../../../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  setConsentDateTime, setPatientConsentProceedClicked,
  setPatientSignature, setSelectedConsentOptions, setSelectedConsentOptionStyle,
} from '../../../../../../store/actions/newAdmissionAction';
import { FormInputMobile, FormTextInput } from '../../../../../../shared/components/crmFormElement/FormInput';
import FormTextArea from '../../../../../../shared/components/crmFormElement/FormTextArea/FormTextArea';
import { ApplicationDetailModel } from '../../../../../../store/models/applicationDetailModel';
import './ConsentSignature.less';
import { formattedMobileNumber, getRandomStyle } from '../../../../../../utils/utility';
import { fetchFileBytesData } from '../../../../../../store/actions/inventoryAction';
import { uploadStyledSignature } from '../../../service/crmUtils';
import { toSentenceCase } from '../../../../../../utils/customPipeUtil';

const { Panel } = Collapse;

const ConsentPanel = ({ formValues }: any): ReactElement => {
  const { newAdmission: { submitDisabled, consentOption, patientConsentProceedClicked, consentOptionStyle, isDraftSelected,
    patientSignature, admissionFormVal, consentDateTime, updatedMemberName,
  } }
    = useSelector((state: RootState): any => state);
  const appDetails = useSelector(
    (state: RootState): ApplicationDetailModel => state.applicationDetail,
  );
  const [consentForm] = Form.useForm();
  const { mobile: admissionFormMobileNo } = formValues?.getFieldsValue() || {};
  const { mobile: patientMobileNo } = admissionFormVal?.customerDetail;
  const memberName = toSentenceCase(updatedMemberName);
  const consentFormMobileNo = patientMobileNo || admissionFormMobileNo;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeKey, setActiveKey] = useState<string[]>([]);
  const [fileByte, setFileByte] = useState<string>('');
  const [isDateSelected, setIsDateSelected] = useState<any>(null);
  const [isSubmitBtnVisible, setIsSubmitBtnVisible] = useState(false);
  const [showRandomOption, setShowRandomOption] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [isChecked, setIsCheckboxChecked] = useState(false);
  const textAreaRef = useRef<any>(null);
  const patientSignGuid = patientSignature?.[0]?.response?.fileUploadDetails?.[0]?.fileGuid || '';
  const consentMobileNo = consentForm?.getFieldsValue(['contactNumber'])?.contactNumber;
  const contactNumber = formattedMobileNumber(consentFormMobileNo);
  const { formattedDate, formattedTime } = consentDateTime || {};
  const dispatch = useDispatch();

  const shouldProceedWithInformedConsent = (): boolean => {
    return (
      consentOption === PATIENT_SIGNATURE_CONSTANTS.INFORMED_VIA_CONSENT &&
      patientConsentProceedClicked
    );
  };

  useEffect((): any => {
    if (isDraftSelected) {
      setIsDateSelected(patientSignature?.[0]);
      getFileBytes(patientSignGuid);
    }
  }, [isDraftSelected]);

  useEffect((): any => {
    if (consentOption !== '') {
      setFileByte('');
    }
  }, [consentOption]);

  useEffect((): any => {
    if (patientConsentProceedClicked) {
      getFileBytes(patientSignGuid);
      consentForm.setFieldsValue({
        date: formattedDate || '',
        time: formattedTime || '',
        signature: (shouldProceedWithInformedConsent()) ? memberName : undefined,
      });
    }
  }, [patientConsentProceedClicked, isDateSelected, patientSignature, memberName]);

  useEffect((): any => {
    if (contactNumber !== consentMobileNo) {
      consentForm.setFieldsValue({
        contactNumber,
      });
    }
  }, [contactNumber]);

  useEffect((): any => {
    consentForm.setFieldsValue({
      patientName: memberName,
    });
  }, [memberName]);

  useEffect((): any => {
    // For generating random styles in draft flow
    if (consentOptionStyle && patientConsentProceedClicked) {
      setShowRandomOption(false);
    }
  }, [consentOptionStyle, patientConsentProceedClicked]);

  useEffect((): any => {
    if (shouldProceedWithInformedConsent()) {
      formattedMobileNumber(contactNumber);
      setTimeout((): any => {
        const current = textAreaRef?.current;
        const textAreaElement = textAreaRef?.current?.resizableTextArea?.textArea;
        const consentFontSize = consentOptionStyle?.fontSize;
        const consentFontFamily = consentOptionStyle?.fontFamily;
        if (current) {
          const randomStyle = getRandomStyle(consentOptionStyle, showRandomOption);
          textAreaElement.style.fontSize = randomStyle.fontSize;
          textAreaElement.style.fontFamily = randomStyle.fontFamily;
          textAreaElement.style.fontWeight = randomStyle.fontWeight;
          textAreaElement.style.display = 'flex';
          textAreaElement.style.justifyItems = memberName?.length > 26 ? 'normal' : 'center';
          textAreaElement.style.paddingTop = memberName?.length > 26 ? '0px' : '20px';
        }
        if (shouldProceedWithInformedConsent()) {
          const element = {
            fontSize: textAreaElement?.style?.fontSize,
            fontFamily: textAreaElement?.style?.fontFamily,
            fontWeight: textAreaElement?.style?.fontWeight,
          };
          if (textAreaElement) {
            dispatch(setSelectedConsentOptionStyle(element));
          }
        }
        if (memberName) {
          uploadStyledSignature(memberName,
            (textAreaElement?.style?.fontSize || consentFontSize),
            (textAreaElement?.style?.fontFamily || consentFontFamily));
        }
      }, 200);
    }
  }, [patientConsentProceedClicked, activeKey]);

  const getFileBytes = async (imgId: string): Promise<void> => {
    if (imgId) {
      const fileIdentifiers = [imgId];
      dispatch(fetchFileBytesData(APPLICATION_ID, fileIdentifiers, onFetchSuccess));
    }
  };
  const onFetchSuccess = (response: any): void => {
    setShowLoader(false);
    setFileByte(response?.fileDetails[0]?.fileBytes);
  };

  const handleCollapseChange = (key: string | string[]): void => {
    const expandedActiveKey = Array.isArray(key) ? key : [key];
    if (!submitDisabled) {
      setActiveKey(expandedActiveKey);
    } else {
      setActiveKey([]);
    }
  };

  const showModal = (): void => {
    setIsModalVisible(true);
    dispatch(setPatientConsentProceedClicked(false));
    setShowRandomOption(true);
  };

  const handleModalCancel = (): void => {
    setIsModalVisible(false);
  };

  const onDelete = (): any => {
    setFileByte('');
    setIsSubmitBtnVisible(false);
    setIsDateSelected(null);
    localStorage?.removeItem(INVENTROY_CONSTANTS.patient_signature);
    dispatch(setSelectedConsentOptions(''));
    dispatch(setSelectedConsentOptionStyle({}));
    dispatch(setPatientConsentProceedClicked(false));
    dispatch(setConsentDateTime({}));
    dispatch(setPatientSignature([]));
    consentForm.resetFields(['signature', 'date', 'time']);
    if (textAreaRef.current && textAreaRef.current instanceof HTMLTextAreaElement) {
      textAreaRef.current.value = '';
    }
  };

  const panelHeader = (
    <div className={submitDisabled ? 'mouse-pointer-default' : ''}>
      <span className={`${submitDisabled ? 'faded-title' : 'patient-sign-panel-header'}`}>
        {PATIENT_SIGNATURE_CONSTANTS.TITLE}
      </span>
    </div>
  );

  const getPopoverContent = (): ReactElement => (
    <>
      <p className="patient-sign-header-text">Section Disabled</p>
      <hr className="patient_signature_hr" />
      <div className="patient-sign-content">
        Please fill all the mandatory fields <br />to enable this section
      </div>
    </>
  );

  const renderPopover = (child: ReactNode, overlayClassName: string): ReactElement => (
    <Popover
      placement="top"
      trigger="hover"
      overlayClassName={overlayClassName}
      content={getPopoverContent()}
      mouseEnterDelay={0}
      mouseLeaveDelay={0}
      getPopupContainer={(trigger: HTMLElement): HTMLElement =>
        trigger.parentNode as HTMLElement
      }
    >
      <div>{child}</div>
    </Popover>
  );

  const renderCollapsiblePanel = (): ReactElement => {
    return (
      <Collapse
        accordion={true}
        expandIcon={({ isActive }: any): any => {
          const icon = isActive ? <Minus_Icon /> : submitDisabled ? <Disabled_Plus_Icon /> : <Plus_Icon />;
          const expandIconElement = (
            <span className={`${submitDisabled ? 'disabled-action-icons' : 'action-icons'}`}>
              {icon}
            </span>
          );
          return submitDisabled
            ? renderPopover(expandIconElement, 'patient-sign-popover-icon')
            : expandIconElement;
        }}
        expandIconPosition="right"
        activeKey={activeKey}
        onChange={handleCollapseChange}
        className={`${submitDisabled ? 'mouse-pointer-default-panel' : ''} patientSignatureCollapse`}
      >
        <Panel
          header={
            submitDisabled
              ? renderPopover(<div className="popover-with-header">{panelHeader}</div>, 'patient-sign-popover')
              : panelHeader
          }
          key="1"
          className={submitDisabled ? 'grayed-text' : 'blue-panel-header'}
        >
          <Form form={consentForm} layout="vertical">
            <Row gutter={[16, 16]} className="list-row-container">
              <Col span={24} className="agreement-list">
                <div className="scrollable-list">
                  <ol type="a">
                    {PATIENT_CONSENT_LIST?.map((item: string, index: number): ReactElement => (
                      <li key={index}>{item}</li>
                    ))}
                  </ol>
                </div>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <FormTextInput className="benef-search-patient-name"
                  detail={{
                    key: 'patientName',
                    label: 'Patient’s / Insured’s Name',
                    isName: true,
                    isMaxLengthNotApplicable: true,
                    className: 'HISSearchUI',
                    floatLabel: true,
                    disabled: true,
                    floatLabelWithCss: true,
                    placeholder: PATIENT_SIGNATURE_CONSTANTS.PATIENT_NAME_PLACEHOLDER,
                    fieldInFocus: false,
                    required: true,
                  }} />
              </Col>
              <Col span={12}>
                <FormInputMobile
                  detail={{
                    key: 'contactNumber',
                    label: 'Contact Number',
                    placeholder: PATIENT_SIGNATURE_CONSTANTS.CONTACT_NUMBER_PLACEHOLDER,
                    allowClear: false,
                    disabled: true,
                    prefix: '+91 ',
                    showPrefix: true,
                    className: 'HISSearchUI mobile-no-prefix',
                    floatLabel: true,
                    floatLabelWithCss: true,
                    size: 24,
                  }}
                />
              </Col>
              <Col span={15}>
                {(fileByte && patientConsentProceedClicked) ? (
                  <div className="signature-container w-100 columnsTitle">
                    <label
                      className={`floating-label ${fileByte ? 'floating' : 'default-position'}`}
                    >
                      {PATIENT_SIGNATURE_CONSTANTS.PATIENT_INSURED_SIGNATURE}
                    </label>

                    <div
                      contentEditable={false}
                      className="PatientSearchUI textarea-input textarea-style"
                    >
                      {fileByte && (
                        <img
                          className="form-patient-sign-image hospital-seal hospital-seal-img"
                          src={`data:image/jpeg;base64,${fileByte}`}
                          alt="Patient Sign"
                        />
                      )}
                      <p className="patient-sign-uploaded-container">
                        Sign uploaded by {appDetails.getUserName()} {formattedDate}, {formattedTime}
                        {showLoader && <span className="patient-sign-loader patient-sign-left-12" />}</p>
                    </div>
                  </div>

                ) : (
                  <div className={!patientConsentProceedClicked ? 'consent-panel-signature-cont' : 'PatientSearch-consent-UI'}>
                    <FormTextArea
                      ref={textAreaRef}
                      detail={{
                        key: 'signature',
                        label: PATIENT_SIGNATURE_CONSTANTS.PATIENT_INSURED_SIGNATURE,
                        size: 24,
                        contentEditable: false,
                        allowClear: false,
                        disabled: true,
                        className: 'PatientSearchUI textarea-input no-cursor',
                        readOnly: true,
                        floatLabel: true,
                        floatLabelWithCss: true,
                        hideLabel: true,
                        rules: [{ required: true, message: 'Please provide your signature' }],
                      }}
                    />
                    {patientConsentProceedClicked && <p className="signature-meta-text">
                      Consent by {appDetails.getUserName()} {formattedDate}, {formattedTime}
                    </p>}
                  </div>
                )}
                {(patientConsentProceedClicked) &&
                  <div className={consentOption === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1] ? 'justify-end display-flex-item patient-sign-consent-text' : 'signature-action-container'}>
                    {consentOption === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0] && <span onClick={(): void => {
                      setShowLoader(true);
                      getFileBytes(patientSignGuid);
                    }} className="refresh-signature-btn mouse-pointer">{PATIENT_SIGNATURE_CONSTANTS.REFRESH_SIGNATURE_TEXT}</span>}
                    <span onClick={(): void => onDelete()}
                      className="delete-signature-btn mouse-pointer">{PATIENT_SIGNATURE_CONSTANTS.DELETE_SIGNATURE_TEXT}</span>
                  </div>}
              </Col>
              <Col span={5} className="align-content-end">
                <FormTextInput className="benef-search-patient-name"
                  detail={{
                    key: 'date',
                    label: 'Date',
                    isName: true,
                    className: `HISSearchUI patient-sign-placeholder patient-sign-container-bg-color ${patientConsentProceedClicked ? 'patient-sign-input' : ''}`,
                    floatLabel: true,
                    disabled: true,
                    placeholder: INDIAN_DATE_FORMAT,
                    floatLabelWithCss: true,
                    hideLabel: true,
                    fieldInFocus: false,
                    required: false,
                    allowClear: false,
                  }} />
              </Col>
              <Col span={4} className="align-content-end">
                <FormTextInput className="benef-search-patient-name"
                  detail={{
                    key: 'time',
                    label: 'Time',
                    isName: true,
                    className: `HISSearchUI patient-sign-placeholder patient-sign-container-bg-color ${patientConsentProceedClicked ? 'patient-sign-input' : ''}`,
                    floatLabel: true,
                    floatLabelWithCss: true,
                    placeholder: PATIENT_SIGNATURE_CONSTANTS.TIME_FORMAT,
                    disabled: true,
                    hideLabel: true,
                    fieldInFocus: false,
                    required: false,
                    allowClear: false,
                  }} />
              </Col>
            </Row>
            {(!isSubmitBtnVisible && !patientConsentProceedClicked) && <Row>
              <Col span={24} className="display-flex-item justify-center">
                <Button type="primary" onClick={showModal} className="signature-button">
                  {PATIENT_SIGNATURE_CONSTANTS.REQUEST_CONSENT_SIGNATURE}
                </Button>
              </Col>
            </Row>}
          </Form>
        </Panel>
      </Collapse>
    );
  };

  return (
    <div className="patient-consent">
      {renderCollapsiblePanel()}
      <ConsentDeclarationModal
        isModalVisible={isModalVisible}
        handleModalCancel={handleModalCancel}
        formValues={formValues}
        setIsDateSelected={setIsDateSelected}
        isDateSelected={isDateSelected}
        setIsSubmitBtnVisible={setIsSubmitBtnVisible}
        isSubmitBtnVisible={isSubmitBtnVisible}
        isChecked={isChecked}
        setIsChecked={setIsCheckboxChecked}
        memberName={memberName}
        setShowRandomOption={setShowRandomOption}
      />
    </div>
  );
};

export default React.memo(ConsentPanel);
